import React, { useEffect, useState } from "react";
import SnapDataBox from "./SnapDataBox";
import TopClient from "./TopClient";
import RevenueChart from "./RevenueChart";
import BrokerHistory from "./BrokerHistory";
import SwiftModal from "../CustomComponents/SwiftModal/SwiftModal";
import { numberFormat, priceFormat, formatSchemeName } from "../../exports/UtilFunctions";
import { Link } from "react-router-dom";
import { ArrowRight2 } from "../CustomComponents/SwiftIcon/Icons";
import { COLOR_VARS } from "../../exports/ColorVars";
import Badge from "../ui/badge";
import SnapDataTable from "./SnapDataTable";
import { getDeviceSize } from "../../exports/InteractiveUtils";
import TabbedTable from "./TabbedTable";

const DEVICE_SIZE = getDeviceSize();

function DashboardTop({
  user_role_type,
  data,
  portfolioData,
  stockData,
  mfData,
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const { customers = [], snapshot } = data;

  return (
    <div className="swift-dashboard-top">
      {user_role_type === "BROKER" || user_role_type === "ADMIN" ? (
        <>
          <BrokerOverview snapshot={snapshot} setHistoryOpen={setHistoryOpen} />
        </>
      ) : (
        <>
          <CustomerOverview
            snapshot={snapshot}
            portfolioData={portfolioData}
            stockData={stockData}
            mfData={mfData}
          />
        </>
      )}
      {user_role_type === "BROKER" && (
        <>
          <div className="top-clients">
            <p className="dash-box-title">Top Clients</p>
            <div className="clients-wrapper">
              {customers.length > 0 ? (
                <>
                  <div className="clients-wrapper-header client-row">
                    <div>Client</div>
                    <div>Investment</div>
                    <div>Current Value</div>
                    <div>Cash Position</div>
                  </div>

                  {customers.map((c, index) => {
                    return <TopClient key={index} data={c} />;
                  })}
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
        </>
      )}
      {/* <div className='dashboard-data-charts'>
                
                <div className='revenue-trend'>
                    <div><p className='dash-box-title'>Revenue Trend</p>
                        <p className='dash-box-subtitle'>This includes broking commissions as well as asset management fees.</p></div>
                    <div className='revenue-chart-wrapper'>
                        <RevenueChart />
                    </div>
                </div>
            </div> */}
      {historyOpen && (
        <>
          <SwiftModal
            top="2%"
            closeModal={() => {
              setHistoryOpen(false);
            }}
          >
            <BrokerHistory
              close={() => {
                setHistoryOpen(false);
              }}
            />
          </SwiftModal>
        </>
      )}
    </div>
  );
}

function BrokerOverview({ snapshot, setHistoryOpen }) {
  return (
    <>
      <div className="dashboard-data-snapshot">
        <p className="dashboard-box-title">Snapshot</p>
        <>
          <span
            id="broking-history"
            onClick={() => {
              setHistoryOpen(true);
            }}
          >
            View History
          </span>
        </>
        <div className="dashboard-snapshot">
          <SnapDataBox
            title="Assets"
            value={priceFormat(snapshot["assets"])}
            class_name="hero"
          />
          <SnapDataBox
            title="Total Active Clients"
            value={snapshot["clients"]}
          />
          <SnapDataBox
            title="Total Transactions"
            value={snapshot["transactions"]}
          />
          <SnapDataBox
            title="Net Cash Flow"
            value={priceFormat(snapshot["netflow"])}
          />
          <SnapDataBox
            title="Turnover"
            value={priceFormat(snapshot["turnover"])}
          />
          <SnapDataBox
            title="Brokerage this month"
            value={priceFormat(snapshot["brokerage"])}
          />
        </div>
      </div>
    </>
  );
}

function CustomerOverview({ snapshot, portfolioData, stockData, mfData }) {
  const { assets, investment_cost, profit_loss } = snapshot;
  const dataArr = portfolioData?.data ?? [];
  const profit_loss_change = parseFloat(
    (parseFloat(assets) / parseFloat(investment_cost) - 1) * 100,
  );
  const lastPortfolio = dataArr[dataArr.length - 1];
  const investmentValue = lastPortfolio?.investment_value;
  const profitLoss = lastPortfolio?.profit_loss;
  const xirr = lastPortfolio?.xirr;
  const stockColumns = ["stock", "value"];
  const mfColumns = ["scheme", "value"];
  const [stockTableData, setStockTableData] = useState(null);
  const [mfTableData, setMfTableData] = useState(null);

  useEffect(() => {
    if (!stockData) return;
    const newStockData = stockData
      .map((d) => ({
        stock: d.StockCode,
        value: d.CurrentValue,
        StockName: d.StockName,
        totalReturns: d.TotalReturn,
      }))
      .sort((a, b) => Number(b.value) - Number(a.value)) 
      .slice(0, 5);

    setStockTableData(newStockData);

    if (!mfData) return;
    const newMfData = mfData
      .filter((d) => d.scheme_name !== "TOTAL")
      .map((d) => ({
        scheme: formatSchemeName(d.scheme_name, 40),
        value: d.investment_value,
        totalReturns: d.total_return,
      }))
      .sort((a, b) => Number(b.value) - Number(a.value))
      .slice(0, 5);
    setMfTableData(newMfData);
  }, [mfData, stockData]);
  const tableTabs = [
    {
      id: "stocks",
      label: "Stocks",
      linkTo: "/account/strategy",
      title: (
        <>
          <div className="icon-link">
            Stocks{" "}
            {/* <ArrowRight2
              className="arrow-icon"
              size={12}
              color={COLOR_VARS["SWIFT_COLOR1"]}
            /> */}
          </div>
          <p style={{ fontSize: "10px", textTransform: "capitalize" }}>
            Top 5 Positions
          </p>
        </>
      ),
      value: priceFormat(assets),
      subtext: (
        <p className={`change ${profit_loss >= 0 ? "positive" : "negative"}`}>
          <span>
            {numberFormat(profit_loss)} ( {numberFormat(profit_loss_change)}% )
          </span>
        </p>
      ),
      columns: stockColumns,
      data: stockTableData,
    },
    {
      id: "mutual_funds",
      label: "Mutual Funds",
      linkTo: "/account/mf/portfolio",
      title: (
        <>
          <div className="icon-link">
            Mutual Funds{" "}
            {/* <ArrowRight2
              className="arrow-icon"
              size={12}
              color={COLOR_VARS["SWIFT_COLOR1"]}
            /> */}
          </div>
          <p style={{ fontSize: "10px", textTransform: "capitalize" }}>
            Top 5 Positions
          </p>
        </>
      ),
      value: priceFormat(investmentValue),
      subtext: (
        <p className={`change ${profitLoss >= 0 ? "positive" : "negative"}`}>
          <span>
            {numberFormat(profitLoss)} ( {numberFormat(xirr)}% )
          </span>
        </p>
      ),
      columns: mfColumns,
      data: mfTableData,
    },
  ];

  return DEVICE_SIZE === "S" ? (
    <>
      <div className="customer-overview">
        {/* 1. Overall Portfolio Summary (Remains untouched) */}
        <Link to="">
          <SnapDataBox
            total={true}
            title={<>Total</>}
            value={priceFormat(
              parseFloat(investmentValue) + parseFloat(assets),
            )}
            subtext={
              <p
                className={`change ${parseFloat(profitLoss) + parseFloat(profit_loss) >= 0 ? "positive" : "negative"}`}
              >
                ({" "}
                {numberFormat(parseFloat(profitLoss) + parseFloat(profit_loss))}{" "}
                )
              </p>
            }
            class_name="hero-huge"
          />
        </Link>

        <TabbedTable tabs={tableTabs} />
      </div>
    </>
  ) : (
    <>
      <div className="customer-overview">
        <Link to="">
          <SnapDataBox
            total={true}
            title={<>Total </>}
            value={priceFormat(
              parseFloat(investmentValue) + parseFloat(assets),
            )}
            subtext={
              <p
                className={`change ${
                  numberFormat(
                    parseFloat(profitLoss) + parseFloat(profit_loss),
                  ) >= 0
                    ? "positive"
                    : "negative"
                }`}
              >
                ({" "}
                {numberFormat(parseFloat(profitLoss) + parseFloat(profit_loss))}{" "}
                )
              </p>
            }
            class_name="hero-huge"
          />{" "}
        </Link>
        <Link to="/account/strategy">
          {/* <SnapDataBox
            title={
              <>
                Stocks{" "}
                <ArrowRight2 size={12} color={COLOR_VARS["SWIFT_COLOR4"]} />
              </>
            }
            value={priceFormat(assets)}
            subtext={
              <p
                className={`change ${profit_loss >= 0 ? "positive" : "negative"}`}
              >
                <span>{numberFormat(profit_loss)}</span>
                <span>{numberFormat(profit_loss_change)}%</span>
              </p>
            }
            class_name="hero-huge"
          /> */}
          <SnapDataTable
            title={
              <>
                <div className="icon-link">
                  Stocks{" "}
                   <ArrowRight2
                    className="arrow-icon"
                    size={12}
                    color={COLOR_VARS["SWIFT_COLOR1"]}
                  /> {" "}
                </div>
                <p style={{ fontSize: "10px", textTransform: "Capitalize" }}>
                  Top 5 Positions
                </p>
              </>
            }
            value={priceFormat(assets)}
            subtext={
              <p
                className={`change ${profit_loss >= 0 ? "positive" : "negative"}`}
              >
                <span>
                  {numberFormat(profit_loss)} ({" "}
                  {numberFormat(profit_loss_change)}% )
                </span>
              </p>
            }
            columns={stockColumns}
            data={stockTableData}
          />
        </Link>
        <Link to="/account/mf/portfolio">
          {" "}
          {/* <SnapDataBox
            title={
              <>
                Mutual Funds{" "}
                <ArrowRight2 size={12} color={COLOR_VARS["SWIFT_COLOR4"]} /> 
              </>
            }
            value={priceFormat(investmentValue)}
            subtext={
              <p
                className={`change ${profitLoss >= 0 ? "positive" : "negative"}`}
              >
                <span>{numberFormat(profitLoss)}</span>
                <span>{numberFormat(xirr)}%</span>
              </p>
            }
            class_name="hero-huge"
          />{" "} */}
          <SnapDataTable
            title={
              <>
                <div className="icon-link">
                  Mutual Funds
                  <ArrowRight2
                    className="arrow-icon"
                    size={12}
                    color={COLOR_VARS["SWIFT_COLOR1"]}
                  />
                </div>
                <p style={{ fontSize: "10px", textTransform: "capitalize" }}>
                  Top 5 Positions
                </p>
              </>
            }
            value={priceFormat(investmentValue)}
            subtext={
              <p
                className={`change ${profitLoss >= 0 ? "positive" : "negative"}`}
              >
                <span>
                  {numberFormat(profitLoss)} ( {numberFormat(xirr)}% )
                </span>
              </p>
            }
            columns={mfColumns}
            data={mfTableData}
          />
        </Link>
      </div>
    </>
  );
}

export default DashboardTop;
